import { useCallback, useEffect, useRef, useState } from 'react';

import { listPublicForms } from '../services/publicForms';

/**
 * A collection's permanent public forms, and whether this keepr has them at
 * all (KPR-513). The list (`GET …/public-forms`) is the API's only word on
 * the feature, so it is read once by the settings page and decides:
 *
 *   status 'absent'  — the API has no such route (404: an API older than
 *                      KPR-471, which is what the live API is until it
 *                      ships). No tab, no volume, nothing asked again.
 *   status 'off'     — the route exists but the server's switch
 *                      (`KEEPR_PUBLIC_FORMS`) is off: `unavailable:
 *                      'public_forms_off'`. No tab either — the owner turns
 *                      public forms on (KPR-518), never a deploy — but the
 *                      volume routes exist, so invite links show theirs.
 *   status 'on'      — the tab, whatever else `unavailable` says (the bot
 *                      check, the collection's intake switch): the tab
 *                      itself says why nothing can be made yet.
 *   status 'unknown' — not asked (not a manager, a new collection) or not
 *                      answered yet, or a refusal that says nothing about
 *                      the feature (a network error): no tab.
 *
 * `enabled` false asks nothing: a reader or writer is never sent to a
 * manage route the API would refuse them.
 */
export default function usePublicForms(collectionId, { enabled = true } = {}) {
	const [state, setState] = useState({ status: 'unknown', data: null });
	const seq = useRef(0);

	const load = useCallback(async () => {
		if (!collectionId || !enabled) return null;
		seq.current += 1;
		const mine = seq.current;
		try {
			const data = await listPublicForms(collectionId);
			if (mine !== seq.current) return data;
			setState({ status: data.unavailable === 'public_forms_off' ? 'off' : 'on', data });
			return data;
		} catch (error) {
			if (mine !== seq.current) return null;
			const status = error?.response?.status;
			// Only a 404 says "no such route". Anything else — a blip, a 500 —
			// keeps what was known, so one failed refresh never hides a tab a
			// manager is standing in.
			if (status === 404) setState({ status: 'absent', data: null });
			return null;
		}
	}, [collectionId, enabled]);

	useEffect(() => {
		setState({ status: 'unknown', data: null });
		if (!collectionId || !enabled) return undefined;
		load();
		return () => { seq.current += 1; };
	}, [collectionId, enabled, load]);

	return { ...state, reload: load };
}
